import { AdUnit, getAdUnitMediatypes } from '@pubstack/common/src/adunit'
import { Checkbox } from '@pubstack/ui/components/Checkbox'
import { FunctionComponent } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import Button from '~/components/Button'
import { Input } from '~/components/Input'
import { Qualifier } from '~/components/Qualifier'
import Table, { handleTableSearch, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { WithClassName } from '~/types/utils'

type AdUnitSelectionTableProps = WithClassName & {
  selectableAdUnits: AdUnit[]
  selectedAdUnits: AdUnit[]
  onAdUnitsSelection: (adUnits: AdUnit[]) => unknown
}
export const AdUnitSelectionTable: FunctionComponent<AdUnitSelectionTableProps> = ({ className, selectableAdUnits, selectedAdUnits, onAdUnitsSelection }) => {
  const {
    resetField,
    control,
    formState: { isDirty },
  } = useForm<{ search: string }>({
    defaultValues: {
      search: '',
    },
  })
  const search = useWatch({ control, name: 'search' })

  const displayedRows = handleTableSearch(selectableAdUnits, search, ['name'])

  const isSelected = (adUnit: AdUnit) => selectedAdUnits.some((a) => a.id === adUnit.id)

  function getUniqueListBy(arr: AdUnit[]) {
    return [...new Map(arr.map((item) => [item['id'], item])).values()]
  }

  const selectedDisplayedCount = displayedRows.filter(isSelected).length
  const allDisplayedSelected = displayedRows.length > 0 && selectedDisplayedCount === displayedRows.length
  const someDisplayedSelected = selectedDisplayedCount > 0 && !allDisplayedSelected

  const onSelectAll = () => {
    if (allDisplayedSelected) {
      onAdUnitsSelection(selectedAdUnits.filter((s) => !displayedRows.some((a) => a.id === s.id)))
    } else {
      onAdUnitsSelection(getUniqueListBy([...selectedAdUnits, ...displayedRows]))
    }
  }

  const columns: TableColumns = [
    {
      name: (
        <div className={'flex flex-row items-center gap-1 whitespace-nowrap'}>
          <Checkbox label={'Ad unit'} checked={allDisplayedSelected} indeterminate={someDisplayedSelected} onChange={onSelectAll} trackingLabel={'adm-sites-stacks-form-all-ad-units-selected-set'} />
        </div>
      ),
      isSortable: false,
    },
    { name: <div className={'flex flex-row items-center gap-1 whitespace-nowrap'}>Ad server name</div>, isSortable: false },
    { name: <div className={'flex flex-row items-center gap-1 whitespace-nowrap'}>Div ID</div>, isSortable: false },
    { name: 'Demand', isSortable: false },
    { name: 'Settings', isSortable: false },
  ]

  const onSelection = (row: AdUnit) => {
    if (isSelected(row)) {
      onAdUnitsSelection(selectedAdUnits.filter((s) => s.id !== row.id))
    } else {
      onAdUnitsSelection([...selectedAdUnits, row])
    }
  }

  const getTooltip = (state: boolean) => {
    return state ? 'allowed' : 'disabled'
  }

  return (
    <>
      <form className={'mb-3 flex shrink-0 grow items-center'}>
        {/*FIXME 2024-07-19 NRA, SLE: when typing enter the page is reloaded*/}
        <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} control={control} />
        {isDirty && (
          <div className={'ml-4 mt-1'}>
            <Button iconName={'close'} variant={'tertiary'} onClick={() => resetField('search')} trackingLabel={'adm-sites-stacks-form-select-ad-units-search-restore'} />
          </div>
        )}
      </form>
      <Table className={className} columns={columns} isLoading={false}>
        {displayedRows.map((row) => (
          <TableRow
            key={row.id}
            clickable={{
              trackingLabel: 'adm-sites-stacks-form-select-ad-units-set',
              onClick: (e) => {
                e.preventDefault()
                e.stopPropagation()
                onSelection(row)
              },
            }}
          >
            <TableCell>
              <div className={'flex flex-row items-center gap-1 whitespace-nowrap'}>
                <Checkbox
                  label={row.name}
                  checked={isSelected(row)}
                  onChange={() => onSelection(row)}
                  onClick={(e) => e.stopPropagation()}
                  trackingLabel={'adm-sites-stacks-form-select-ad-units-set'}
                />
              </div>
            </TableCell>
            <TableCell>
              <div className={'w-[170px] overflow-hidden text-ellipsis whitespace-nowrap'} title={row.adServerAdUnitName}>
                {row.adServerAdUnitName}
              </div>
            </TableCell>
            <TableCell>
              <div className={'w-[170px] overflow-hidden text-ellipsis whitespace-nowrap'} title={row.divId}>
                {row.divId}
              </div>
            </TableCell>
            <TableCell>
              <Qualifier iconName={'header_bidding'} tooltipText={`Header bidding ${getTooltip(row.headerBiddingEnabled)}`} active={row.headerBiddingEnabled} enabled={true} />
            </TableCell>
            <TableCell>
              {getAdUnitMediatypes(row).includes('Instream') ? (
                <div className={'flex gap-2'}>
                  <Qualifier
                    iconName={'outstream'}
                    tooltipText={`Instream ${getTooltip(getAdUnitMediatypes(row).includes('Instream'))}`}
                    active={getAdUnitMediatypes(row).includes('Instream')}
                    enabled={true}
                  />
                </div>
              ) : (
                <div className={'flex gap-2'}>
                  <Qualifier iconName={'dynamic'} tooltipText={`Dynamic ${getTooltip(row.dynamicEnabled)}`} active={row.dynamicEnabled} enabled={true} />
                  <Qualifier
                    iconName={'banner'}
                    tooltipText={`Banner ${getTooltip(getAdUnitMediatypes(row).includes('Banner'))}`}
                    active={getAdUnitMediatypes(row).includes('Banner')}
                    enabled={true}
                  />
                  <Qualifier
                    iconName={'outstream2'}
                    tooltipText={`Outstream ${getTooltip(getAdUnitMediatypes(row).includes('Outstream'))}`}
                    active={getAdUnitMediatypes(row).includes('Outstream')}
                    enabled={true}
                  />
                  <Qualifier iconName={'native'} tooltipText={`Native ${getTooltip(!!row.nativeTemplate)}`} active={!!row.nativeTemplate} enabled={true} />
                </div>
              )}
            </TableCell>
          </TableRow>
        ))}
      </Table>
    </>
  )
}
